import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { MainLayout } from '../layouts/MainLayout';
import {
  projectService,
  MA_METHOD_LABELS,
  PROJECT_STATUS_LABELS,
  COUNTRY_LABELS,
  type ProjectFilters,
  type CreateProjectRequest,
} from '../services/api/projectService';
import type { Project, MAMethod, ProjectStatus, Country } from '../types';

const statusConfig: Record<ProjectStatus, { label: string; color: 'info' | 'warning' | 'success' | 'default' | 'error' }> = {
  planning: { label: '検討中', color: 'info' },
  in_progress: { label: '進行中', color: 'warning' },
  completed: { label: '完了', color: 'success' },
  cancelled: { label: '中止', color: 'default' },
};

export function ProjectListPage() {
  const navigate = useNavigate();

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [maMethodFilter, setMaMethodFilter] = useState<MAMethod | ''>('');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | ''>('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form state
  const [newProject, setNewProject] = useState<{
    name: string;
    maMethod: MAMethod | '';
    targetCompany: string;
    targetCountry: Country;
    description: string;
  }>({
    name: '',
    maMethod: '',
    targetCompany: '',
    targetCountry: 'japan',
    description: '',
  });

  // Fetch projects from API
  const fetchProjects = useCallback(async (filters?: ProjectFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await projectService.getProjects(filters);
      setProjects(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : '案件一覧の取得に失敗しました';
      setError(message);
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      const filters: ProjectFilters = {};
      if (searchQuery) filters.keyword = searchQuery;
      if (maMethodFilter) filters.maMethod = maMethodFilter;
      if (statusFilter) filters.status = statusFilter;

      fetchProjects(filters);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, maMethodFilter, statusFilter, fetchProjects]);

  const handleRefresh = () => {
    const filters: ProjectFilters = {};
    if (searchQuery) filters.keyword = searchQuery;
    if (maMethodFilter) filters.maMethod = maMethodFilter;
    if (statusFilter) filters.status = statusFilter;
    fetchProjects(filters);
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProject.name || !newProject.maMethod || !newProject.targetCompany) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const requestData: CreateProjectRequest = {
        name: newProject.name,
        maMethod: newProject.maMethod as MAMethod,
        targetCompany: newProject.targetCompany,
        targetCountry: newProject.targetCountry,
        description: newProject.description || undefined,
      };

      const createdProject = await projectService.createProject(requestData);

      setIsModalOpen(false);
      setNewProject({
        name: '',
        maMethod: '',
        targetCompany: '',
        targetCountry: 'japan',
        description: '',
      });

      // Navigate to the new project detail page
      navigate(`/projects/${createdProject.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : '案件の作成に失敗しました';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCardClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSubmitError(null);
    setNewProject({
      name: '',
      maMethod: '',
      targetCompany: '',
      targetCountry: 'japan',
      description: '',
    });
  };

  // Render loading skeletons
  const renderLoadingSkeletons = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        },
        gap: 3,
      }}
    >
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Card key={i}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="rounded" width={60} height={24} />
            </Box>
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="40%" />
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={600}>
            案件一覧
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={handleRefresh} disabled={isLoading} title="更新">
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsModalOpen(true)}
            >
              新規作成
            </Button>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filter Bar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="案件名・対象企業名で検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>M&A手法</InputLabel>
              <Select
                value={maMethodFilter}
                label="M&A手法"
                onChange={(e) => setMaMethodFilter(e.target.value as MAMethod | '')}
              >
                <MenuItem value="">すべて</MenuItem>
                {Object.entries(MA_METHOD_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>ステータス</InputLabel>
              <Select
                value={statusFilter}
                label="ステータス"
                onChange={(e) => setStatusFilter(e.target.value as ProjectStatus | '')}
              >
                <MenuItem value="">すべて</MenuItem>
                {Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Project Cards Grid */}
        {isLoading ? (
          renderLoadingSkeletons()
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
                gap: 3,
              }}
            >
              {projects.map((project) => (
                <Card
                  key={project.id}
                  sx={{
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                  onClick={() => handleCardClick(project.id)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 500 }}>
                        {project.name}
                      </Typography>
                      <Chip
                        label={statusConfig[project.status].label}
                        color={statusConfig[project.status].color}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      対象企業: {project.targetCompany}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {MA_METHOD_LABELS[project.maMethod]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        |
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {COUNTRY_LABELS[project.targetCountry]}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {projects.length === 0 && !error && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary">
                  該当する案件が見つかりません
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Create Project Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          新規案件作成
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box component="form" onSubmit={handleCreateProject}>
          <DialogContent dividers>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
                {submitError}
              </Alert>
            )}
            <TextField
              label="案件名"
              placeholder="例: ABC社買収案件"
              value={newProject.name}
              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel>M&A手法</InputLabel>
              <Select
                value={newProject.maMethod}
                label="M&A手法"
                onChange={(e) => setNewProject({ ...newProject, maMethod: e.target.value as MAMethod })}
              >
                {Object.entries(MA_METHOD_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="対象企業名"
              placeholder="例: ABC株式会社"
              value={newProject.targetCompany}
              onChange={(e) => setNewProject({ ...newProject, targetCompany: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>対象国・地域</InputLabel>
              <Select
                value={newProject.targetCountry}
                label="対象国・地域"
                onChange={(e) => setNewProject({ ...newProject, targetCountry: e.target.value as Country })}
              >
                {Object.entries(COUNTRY_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="概要"
              placeholder="案件の概要を入力（任意）"
              value={newProject.description}
              onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} disabled={isSubmitting}>
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !newProject.name || !newProject.maMethod || !newProject.targetCompany}
            >
              {isSubmitting ? <CircularProgress size={24} /> : '作成'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </MainLayout>
  );
}
